import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import { adminAPI, branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { KpiCard } from '@/components/KpiCard';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Pencil, ListChecks, CheckCircle, Clock, AlertTriangle, Users, FileText } from 'lucide-react';

const DAILY_STATUS = {
  present: { label: 'Present', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  late: { label: 'Late', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  half_day: { label: 'Half Day', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  absent: { label: 'Absent', cls: 'bg-red-100 text-red-700 border-red-200' },
  not_started: { label: 'Not started', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  pending: { label: 'Awaiting', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  off: { label: 'Day Off', cls: 'bg-slate-50 text-slate-400 border-slate-200' },
  contractor: { label: 'Contractor', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  upcoming: { label: '—', cls: 'bg-slate-50 text-slate-400 border-slate-200' },
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const shiftDate = (d, days) => { const dt = new Date(d + 'T00:00:00'); dt.setDate(dt.getDate() + days); return dt.toISOString().slice(0, 10); };

export default function DailyOverviewPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ check_in: '', check_out: '' });
  const [tasksRow, setTasksRow] = useState(null);

  useEffect(() => {
    branchAPI.list().then(r => {
      setBranches(r.data);
      if (r.data.length > 0) setSelectedBranch(user?.branch_id || r.data[0].id);
    }).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, [user?.branch_id]);

  const load = useCallback(async () => {
    if (!selectedBranch) return;
    try {
      const res = await adminAPI.dailyOverview(selectedBranch, date);
      setData(res.data);
    } catch { toast.error('Failed to load daily overview'); }
  }, [selectedBranch, date]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const count = (s) => rows.filter(r => r.status === s).length;

  const openEdit = (row) => {
    setEditForm({
      check_in: to24h(row.check_in_display),
      check_out: to24h(row.check_out_display),
    });
    setEditRow(row);
  };

  const saveEdit = async () => {
    try {
      await adminAPI.fixAttendance({
        employee_id: editRow.employee_id,
        branch_id: selectedBranch,
        date,
        check_in_time_local: editForm.check_in || null,
        check_out_time_local: editForm.check_out || null,
      });
      toast.success(`Updated ${editRow.employee_name}'s attendance`);
      setEditRow(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  if (loading) return <div className="h-32 bg-muted animate-pulse rounded-xl" />;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Daily Overview</h1>
          <p className="text-muted-foreground">Everything in one place — attendance, status & tasks per day {data?.local_time && <span className="ml-1">· Branch time {data.local_time}</span>}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedBranch} onValueChange={setSelectedBranch} data-testid="daily-branch-select">
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setDate(d => shiftDate(d, -1))} data-testid="daily-prev-day"><ChevronLeft size={16} /></Button>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-[150px]" data-testid="daily-date-input" />
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setDate(d => shiftDate(d, 1))} data-testid="daily-next-day"><ChevronRight size={16} /></Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Present" value={count('present') + count('late') + count('half_day')} icon={CheckCircle} />
        <KpiCard title="Late" value={count('late')} icon={Clock} />
        <KpiCard title="Absent" value={count('absent')} icon={AlertTriangle} />
        <KpiCard title="Not in yet" value={count('not_started') + count('pending')} icon={Users} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">{data?.branch?.name} — {date}</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No employees for this branch.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead>Check-out</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead>Tasks</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Fix</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => {
                    const st = DAILY_STATUS[r.status] || DAILY_STATUS.upcoming;
                    return (
                      <TableRow key={r.employee_id} data-testid={`daily-row-${r.employee_id}`}>
                        <TableCell>
                          <div className="font-medium">{r.employee_name}</div>
                          <div className="text-xs text-muted-foreground capitalize">{r.staff_role || r.role}</div>
                        </TableCell>
                        <TableCell className="tabular-nums">{r.check_in_display || '—'}</TableCell>
                        <TableCell className="tabular-nums">
                          {r.check_out_display || '—'}
                          {r.missed_checkout && <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 text-amber-700">auto 7PM</Badge>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.hours_worked != null ? `${r.hours_worked}h` : '—'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setTasksRow(r)} data-testid={`daily-tasks-${r.employee_id}`}>
                            <ListChecks size={14} /> {r.worklogs?.length || 0}
                          </Button>
                        </TableCell>
                        <TableCell><Badge variant="outline" className={st.cls} data-testid={`daily-status-${r.employee_id}`}>{st.label}</Badge></TableCell>
                        <TableCell className="text-right">
                          {r.employment_type !== 'contractor' && (
                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)} title="Fix check-in/out" data-testid={`daily-edit-${r.employee_id}`}>
                              <Pencil size={14} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit attendance dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Fix Attendance</DialogTitle>
            <DialogDescription>{editRow?.employee_name} · {date}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Check-in</Label>
              <Input type="time" value={editForm.check_in} onChange={e => setEditForm({ ...editForm, check_in: e.target.value })} data-testid="fix-checkin-input" />
            </div>
            <div className="space-y-2">
              <Label>Check-out</Label>
              <Input type="time" value={editForm.check_out} onChange={e => setEditForm({ ...editForm, check_out: e.target.value })} data-testid="fix-checkout-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Leave check-in blank to remove the record for this day. Late status is recalculated from the branch schedule.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="fix-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tasks for the day dialog */}
      <Dialog open={!!tasksRow} onOpenChange={(o) => !o && setTasksRow(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{tasksRow?.employee_name} — Tasks on {date}</DialogTitle>
            <DialogDescription>Task updates this employee logged on the selected day.</DialogDescription>
          </DialogHeader>
          {(!tasksRow?.worklogs || tasksRow.worklogs.length === 0) ? (
            <p className="text-sm text-muted-foreground py-4">No task updates logged for this day.</p>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {tasksRow.worklogs.map(lg => (
                <div key={lg.id} className="border-l-2 border-primary/30 pl-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-primary">{lg.task_title}</span>
                    <span className="text-xs text-muted-foreground">{lg.progress_after}%</span>
                  </div>
                  <p className="text-foreground/80 mt-0.5">{lg.summary}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    {lg.hours_spent > 0 && <span>{lg.hours_spent}h</span>}
                    {lg.files_touched && <span className="flex items-center gap-1"><FileText size={10} /> {lg.files_touched}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Convert "hh:mm AM/PM" display to "HH:MM" for time input
function to24h(display) {
  if (!display) return '';
  const m = display.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ap = (m[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
}
